/**
 * src/components/TrialBanner.tsx
 *
 * Bandeau persistant + popup de décision pour le mode essai gratuit.
 * Cf. spec/trial-mode-with-coupon.md §5.2 (banner) + §5.3 (popup).
 *
 * Monté dans Layout (visible sur toute l'app pour un user en essai).
 * S'auto-gère : fetch /api/account/subscription/trial-status au montage.
 *
 *  - inTrial=false → render null (pas d'essai en cours).
 *  - inTrial=true, requiresDecision=false → bandeau "X/N sessions · expire le …".
 *  - inTrial=true, requiresDecision=true → popup bloquant (limite atteinte ou J+14).
 *
 * Le banner expose un bouton "Activer mon abonnement" qui ouvre le popup de
 * décision (avec les 2 choix activer/décliner). Le popup est aussi déclenché
 * automatiquement quand requiresDecision passe à true.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { accountApi } from "../lib/api";
import { Gift, Loader2, X } from "lucide-react";

type TrialStatus = Awaited<ReturnType<typeof accountApi.getTrialStatus>>;

function formatDate(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long" });
}

export function TrialBanner() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TrialStatus | null>(null);
  const [showDecision, setShowDecision] = useState(false);
  const [acting, setActing] = useState<null | "activate" | "decline">(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    accountApi.getTrialStatus()
      .then((s) => {
        setStatus(s);
        // Popup auto si une décision est requise (limite atteinte ou temps écoulé)
        if (s.inTrial && s.requiresDecision) setShowDecision(true);
      })
      .catch(() => setStatus({ inTrial: false }));
  }, []);

  if (!status?.inTrial) return null;

  const { sessionsUsed = 0, sessionsLimit = 0, trialEndsAt, canActivate, canDecline } = status;

  const handleActivate = async () => {
    setActing("activate"); setError(null);
    try {
      await accountApi.activateTrial();
      window.location.reload();
    } catch (e: any) {
      setError(e.message);
      setActing(null);
    }
  };

  const handleDecline = async () => {
    setActing("decline"); setError(null);
    try {
      await accountApi.declineTrial();
      window.location.reload();
    } catch (e: any) {
      setError(e.message);
      setActing(null);
    }
  };

  return (
    <>
      {/* Bandeau persistant */}
      <div className="w-full bg-[#F6C847] text-[#384872] text-sm">
        <div className="container mx-auto px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 shrink-0" />
            <span className="font-medium">
              Essai gratuit · <strong>{sessionsUsed}/{sessionsLimit}</strong> simulation{sessionsLimit > 1 ? "s" : ""} utilisée{sessionsUsed > 1 ? "s" : ""}
              {trialEndsAt && <> · expire le {formatDate(trialEndsAt)}</>}
            </span>
          </div>
          {canActivate && (
            <button
              onClick={() => setShowDecision(true)}
              className="rounded-md bg-[#384872] text-white px-3 py-1 text-xs font-semibold hover:bg-[#2d3a5c] transition-colors"
            >
              Activer mon abonnement →
            </button>
          )}
        </div>
      </div>

      {/* Popup de décision */}
      {showDecision && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-[#384872] flex items-center gap-2">
                <Gift className="h-5 w-5" /> Votre essai gratuit
              </h3>
              {/* Croix de fermeture uniquement si décision PAS obligatoire (sinon bloquant) */}
              {!status.requiresDecision && (
                <button onClick={() => setShowDecision(false)} className="p-1.5 rounded hover:bg-slate-100">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              )}
            </div>
            <div className="px-6 py-5 space-y-4">
              {status.requiresDecision ? (
                <p className="text-sm text-slate-700">
                  {status.timeExpired
                    ? "La durée de votre essai gratuit est arrivée à échéance."
                    : `Vous avez utilisé vos ${sessionsLimit} simulations d'essai.`}
                  {" "}Pour continuer à utiliser BizCoach, activez votre abonnement.
                </p>
              ) : (
                <p className="text-sm text-slate-700">
                  Vous êtes en essai gratuit ({sessionsUsed}/{sessionsLimit} simulations utilisées).
                  Vous pouvez activer votre abonnement dès maintenant, ou continuer votre essai.
                </p>
              )}

              {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded">{error}</div>}

              <div className="space-y-3">
                {canActivate && (
                  <button
                    onClick={handleActivate}
                    disabled={!!acting}
                    className="w-full rounded-lg bg-[#1E7A3C] text-white px-4 py-3 font-semibold hover:bg-[#176030] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {acting === "activate" ? <Loader2 className="h-4 w-4 animate-spin" /> : "✓"}
                    Activer mon abonnement
                  </button>
                )}
                {canDecline && (
                  <button
                    onClick={handleDecline}
                    disabled={!!acting}
                    className="w-full rounded-lg border-2 border-slate-300 text-slate-600 px-4 py-3 font-medium hover:bg-slate-50 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {acting === "decline" ? <Loader2 className="h-4 w-4 animate-spin" /> : "✕"}
                    Ce n'est pas pour moi
                  </button>
                )}
                {status.requiresDecision && canDecline && (
                  <p className="text-xs text-slate-400 text-center">
                    {t("trial.declineConsequence")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
